import { BTSelectItem } from "types/apiResponse/apiResponse";
import { type AccountingValidationStatus } from "types/enum";

import { type IBillPurchaseOrderLinkResponse } from "commonComponents/utilities/Grid/common/columnImplementations/columnBillPurchaseOrderLink";

import { type default as MassBillPaymentAPIExample } from "entity/massPoPayment/MassBillPayment/MassBillPayment.api.json";
import { type default as MassPaymentValidationAPIExample } from "entity/massPoPayment/MassBillPayment/MassBillPayment.api.validation.json";
import { type default as MassPayOfflinePostPayment } from "entity/massPoPayment/MassPayOfflinePostPayment/MassPayOfflinePostPayment.api.json";
import { type PaymentMethodDto } from "entity/onlinePayment/OnlinePaymentSettings/OnlinePaymentSettings.api.types";

export class MassBillPaymentRecipientResponse {
    constructor(data: (typeof MassBillPaymentAPIExample.recipients)[0]) {
        this.subId = data.subId;
        this.recipientName = data.recipientName;
        this.recipientEmail = data.recipientEmails ?? [];
        this.relatedEntities = data.relatedEntities;
        this.paymentAmount = data.paymentAmount;
        this.memo = data.memo ?? "";
        this.recipientPhone = data.recipientPhone ?? "";
        this.autoProcessAch = data.autoProcessAch ?? false;
        this.allowChecks = data.allowChecks ?? true;
        this.builderAddedBankAccount = data.builderAddedBankAccount ?? false;
        this.availableCredits =
            (data as { availableCredits?: IBillPurchaseOrderLinkResponse[] }).availableCredits ??
            [];
    }

    subId: number;
    recipientName: string;
    recipientEmail: string[];
    relatedEntities: IBillPurchaseOrderLinkResponse[];
    paymentAmount: number;
    memo: string;
    recipientPhone: string;
    autoProcessAch: boolean;
    allowChecks: boolean;
    builderAddedBankAccount: boolean;
    availableCredits: IBillPurchaseOrderLinkResponse[];
}

export class MassBillPaymentResponse {
    constructor(data: typeof MassBillPaymentAPIExample) {
        this.recipients = data.recipients.map((x) => new MassBillPaymentRecipientResponse(x));
        this.paymentMethods = data.paymentMethods?.map((x: any) => new BTSelectItem(x));
        this.shouldSendToAccounting = data.shouldSendToAccounting;
        this.markAccountingBillAsBillable = data.markAccountingBillAsBillable;

        this.companyName = data.companyName;
        this.street = data.street;
        this.formattedAddressNoStreet = data.formattedAddressNoStreet;
        this.expirationDays = data.expirationDays;
        this.signatureUrl = data.signatureUrl;
        this.logoUrl = data.logoUrl;
        this.achEnabled = data.achEnabled ?? false;
        this.limits = data.limits ? new OutboundLimits(data.limits) : null;
    }
    recipients: MassBillPaymentRecipientResponse[];
    paymentMethods: BTSelectItem[];
    shouldSendToAccounting: boolean;
    markAccountingBillAsBillable: boolean;

    companyName: string;
    street: string;
    formattedAddressNoStreet: string;
    expirationDays: number;
    signatureUrl: string;
    logoUrl: string;
    achEnabled: boolean;
    limits: OutboundLimits | null;
}

/**
 * Net payable total across the whole selection: each recipient's bills total (`paymentAmount`) plus
 * any auto-applied vendor credits (which live in `relatedEntities` with `isBill === false` and carry
 * negative amounts). Evaluated across ALL recipients, not per recipient.
 */
export const getMassPayNetPayableTotal = (recipients: MassBillPaymentRecipientResponse[]): number =>
    recipients.reduce(
        (sum, recipient) =>
            sum +
            recipient.paymentAmount +
            recipient.relatedEntities
                .filter((entity) => !entity.isBill)
                .reduce((creditSum, entity) => creditSum + (entity.amount ?? 0), 0),
        0,
    );

export interface IMassBillPaymentFormValues {
    recipients: MassPayRecipient[];
    selectedPaymentMethodId: number;
}

export type MassBillPaymentActions = "submit" | undefined;

export class MassPayRecipient {
    constructor(data: MassBillPaymentRecipientResponse) {
        this.subId = data.subId;
        this.recipientName = data.recipientName;
        this.recipientEmail = data.recipientEmail[0] ?? "";
        this.relatedEntities = data.relatedEntities;
        this.memo = data.memo;
        this.paymentAmount = data.paymentAmount;
        this.isInEdit = false;
        this.recipientPhone = data.recipientPhone ?? "";
        this.autoProcessAch = data.autoProcessAch;
        this.allowChecks = data.allowChecks;
        this.builderAddedBankAccount = data.builderAddedBankAccount;
        this.availableCredits = data.availableCredits ?? [];
    }

    subId?: number;
    recipientName: string;
    recipientEmail: string;
    memo: string;
    paymentAmount: number;
    relatedEntities: IBillPurchaseOrderLinkResponse[];
    isInEdit?: boolean;
    recipientPhone: string;
    autoProcessAch: boolean;
    allowChecks: boolean;
    builderAddedBankAccount: boolean;
    /**
     * Vendor credits available to apply against this recipient's bills (already filtered by the backend
     * to satisfy the per-credit constraint: each credit's amount < recipient's remaining bills total).
     * Powers the "Apply Credits" button. Auto-applied/pre-selected credits flow through `relatedEntities`
     * with `isBill === false`.
     */
    availableCredits: IBillPurchaseOrderLinkResponse[];
}

export class MassBillPayInFullResponse {
    constructor(data: typeof MassPayOfflinePostPayment) {
        this.purchaseOrderPayments = data.purchaseOrderPayments.map(
            (x) => new PurchaseOrderForPostPayment(x),
        );
        this.accountingSucceeded = data.accountingSucceeded;
        this.accountingName = data.accountingName;
    }

    purchaseOrderPayments: PurchaseOrderForPostPayment[];
    accountingSucceeded: boolean;
    accountingName: string;
}

export interface IMassBillPayInFullFormValues {
    billToCustomer: boolean;
}

export class MassBillPaymentValidationResponse {
    constructor(data: typeof MassPaymentValidationAPIExample) {
        this.entityValidationList = data.entityValidationList.map((x) => new EntityValidation(x));
    }

    entityValidationList: EntityValidation[];
}

export interface IMassPoPaymentRequest {
    paymentRequests: IMassPoPaymentPoRequests[];
    billToCustomer?: boolean;
    paymentMethod?: Partial<PaymentMethodDto>;
}

export interface IMassPoPaymentPoRequests {
    paymentId: number;
    subId?: number;
    recipientName?: string;
    recipientEmail?: string;
    memo?: string;
    recipientPhone?: string;
}

export class PurchaseOrderForPostPayment {
    constructor(data: (typeof MassPayOfflinePostPayment.purchaseOrderPayments)[number]) {
        this.title = data.title;
        this.paidTo = data.paidTo;
        this.paidToId = data.paidToId;
        this.amount = data.amount;
        this.transFeeText = data.transFeeText;
        this.paymentSucceeded = data.paymentSucceeded;
        this.paymentFailed = data.paymentFailed;
        this.paymentErrored = data.paymentErrored;
        this.jobName = data.jobName;
        this.isUnderReview = data.isUnderReview;
    }

    title: string;
    paidTo: string;
    paidToId: number;
    amount: number;
    transFeeText: string;
    paymentSucceeded: boolean;
    paymentFailed: boolean;
    paymentErrored: boolean;
    jobName: string;
    isUnderReview: boolean;
}

class OutboundLimits {
    constructor(data: typeof MassBillPaymentAPIExample.limits) {
        this.monthlyLimit = data.monthlyLimit;
        this.transactionLimit = data.transactionLimit;
        this.monthlyUtilization = data.monthlyUtilization;
        this.monthlyRemaining = data.monthlyRemaining;
    }
    monthlyLimit: number;
    transactionLimit: number;
    monthlyUtilization: number;
    monthlyRemaining: number;
}

class EntityValidation {
    constructor(data: (typeof MassPaymentValidationAPIExample.entityValidationList)[number]) {
        this.entityId = data.entityId;
        this.validationStatus = data.validationStatus;
    }

    entityId: number;
    validationStatus: AccountingValidationStatus;
}
